/**
 * Document-Uploader.
 *
 * Direkter POST gegen /api/upload (multer-Endpoint, nicht tRPC, weil
 * multipart). Liste der bestehenden Dokumente über trpc.documents.listForOwner.
 *
 * Soft-Delete (trpc.documents.delete) und Inline-Anschauen (`/api/download/:id`)
 * sind Standard. Document-Review für Staff lebt in den Admin-Pages.
 */
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '../lib/trpc.js';
import { Button } from './ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import { Field } from './ui/field.js';
import { Label } from './ui/label.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.js';
import { StatusBadge } from './StatusBadge.js';

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
];

const WORKER_TYPES = [
  { value: 'id_card', label: 'Personalausweis' },
  { value: 'passport', label: 'Reisepass' },
  { value: 'residence_permit', label: 'Aufenthaltstitel' },
  { value: 'work_permit', label: 'Arbeitserlaubnis / Zusatzblatt' },
  { value: 'social_security', label: 'SV-Ausweis' },
  { value: 'tax_certificate', label: 'Steuerbescheinigung' },
  { value: 'health_insurance', label: 'Mitgliedsbescheinigung Krankenkasse' },
  { value: 'bank_certificate', label: 'Kontoinhaber-Nachweis' },
  { value: 'qualification', label: 'Zeugnis / Qualifikation' },
  { value: 'driving_license', label: 'Führerschein' },
  { value: 'health_certificate', label: 'Gesundheitszeugnis' },
  { value: 'disability_certificate', label: 'Schwerbehindertenausweis' },
  { value: 'criminal_record', label: 'Führungszeugnis' },
  { value: 'other', label: 'Sonstiges' },
] as const;

const CLIENT_TYPES = [
  { value: 'client_company_register', label: 'Handelsregisterauszug' },
  { value: 'client_vat_certificate', label: 'USt-Bescheinigung' },
  { value: 'client_risk_assessment', label: 'Gefährdungsbeurteilung' },
  { value: 'other', label: 'Sonstiges' },
] as const;

const formatBytes = (b: number) =>
  b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

type DocType = (typeof WORKER_TYPES)[number]['value'] | (typeof CLIENT_TYPES)[number]['value'];

export function DocumentUploader({
  ownerType,
  ownerId,
  readOnly,
}: {
  ownerType: 'worker' | 'client';
  ownerId: string;
  readOnly?: boolean;
}) {
  const utils = trpc.useUtils();
  const docs = trpc.documents.listForOwner.useQuery({ ownerType, ownerId });
  const deleteDoc = trpc.documents.delete.useMutation({
    onSuccess: () => utils.documents.listForOwner.invalidate({ ownerType, ownerId }),
  });

  const types = ownerType === 'worker' ? WORKER_TYPES : CLIENT_TYPES;
  const [docType, setDocType] = useState<DocType>(types[0].value);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!ALLOWED_MIME.includes(file.type)) {
      toast.error(`Dateityp ${file.type} nicht erlaubt`);
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Datei zu groß (max ${formatBytes(MAX_BYTES)})`);
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('ownerType', ownerType);
      fd.append('ownerId', ownerId);
      fd.append('type', docType);

      const res = await fetch('/api/upload', { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Upload fehlgeschlagen');
      }
      toast.success('Dokument hochgeladen');
      await utils.documents.listForOwner.invalidate({ ownerType, ownerId });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dokumente</CardTitle>
        <CardDescription>
          PDF, JPG, PNG, HEIC. Max {formatBytes(MAX_BYTES)}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!readOnly && (
          <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
            <Field label="Dokumenttyp" htmlFor="docType">
              <Select value={docType} onValueChange={(v) => setDocType(v as DocType)}>
                <SelectTrigger id="docType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div className="space-y-1.5">
              <Label htmlFor="file">Datei wählen</Label>
              <input
                id="file"
                ref={fileRef}
                type="file"
                accept={ALLOWED_MIME.join(',')}
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                }}
                className="text-sm file:mr-2 file:rounded file:border file:border-[var(--border)] file:bg-background file:px-3 file:py-1.5 file:text-foreground"
              />
            </div>
          </div>
        )}

        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Hochgeladen</h4>
          {docs.isLoading && (
            <p className="text-xs text-[var(--muted-foreground)]">Lade …</p>
          )}
          {docs.data && docs.data.length === 0 && (
            <p className="text-xs text-[var(--muted-foreground)]">
              Noch keine Dokumente hochgeladen.
            </p>
          )}
          {docs.data?.map((doc) => {
            const all = [...WORKER_TYPES, ...CLIENT_TYPES];
            const label = all.find((t) => t.value === doc.type)?.label ?? doc.type;
            return (
              <div
                key={doc.id}
                className="flex items-center justify-between gap-3 rounded-md border border-[var(--border)] bg-card p-3"
              >
                <div className="min-w-0 space-y-0.5">
                  <p className="text-sm font-medium truncate">{doc.originalFilename}</p>
                  <p className="text-xs text-[var(--muted-foreground)] flex items-center gap-2">
                    <span>{label}</span>
                    <span>·</span>
                    <span>{formatBytes(doc.sizeBytes)}</span>
                    <StatusBadge status={doc.status} />
                  </p>
                </div>
                <div className="flex gap-2">
                  <a
                    href={`/api/download/${doc.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs underline text-[var(--muted-foreground)] hover:text-foreground"
                  >
                    Ansehen
                  </a>
                  {!readOnly && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={deleteDoc.isPending}
                      onClick={() => {
                        if (confirm(`Dokument "${doc.originalFilename}" entfernen?`)) {
                          deleteDoc.mutate({ documentId: doc.id });
                        }
                      }}
                    >
                      Entfernen
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
